/**
 * Tone of the week, the eight-tone Octoechos cycle anchored to Antipascha.
 *
 *   Antipascha (Pascha+7)              → Tone 1
 *   Sunday of the Myrrh-bearing Women  → Tone 2
 *   Sunday of the Paralytic            → Tone 3
 *   Sunday of the Samaritan Woman      → Tone 4
 *   Sunday of the Blind Man            → Tone 5
 *   Sunday of the Holy Fathers (week 6)→ Tone 6
 *   Sunday of the Holy Fathers / All-Sts→ Tone 7
 *   All Saints Sunday (Pascha+56)      → Tone 8
 *   week after All Saints              → Tone 1, then cycles 1..8 indefinitely
 *
 * During Bright Week (Pascha .. Pascha+6) and Holy Week (Pascha-7 .. Pascha-1)
 * there is no "tone of the week", services use the Paschal/Triodion books
 * directly. We return `null` and a human-readable `season` for those windows.
 *
 * The Lenten Sundays (Sunday of Orthodoxy through Palm Sunday) traditionally
 * use specific tones from the Triodion that don't fit the modular cycle. The
 * engine documents this as a limitation and falls back to "Triodion" rather
 * than reporting a misleading tone for those weeks.
 */
import { paschaGregorian } from './pascha.js';
import { addDays, diffDays, isBefore, isOnOrAfter } from './dates.js';
const TRIODION_OFFSET_FROM_PASCHA = -70; // Sunday of the Publican and the Pharisee
export function toneForDate(date) {
    const thisYearPascha = paschaGregorian(date.year);
    // The cycle spans last year's Antipascha → this year's Antipascha. For Jan-Apr
    // dates (before this year's Antipascha) we anchor on last year's Pascha.
    const lastYearPascha = paschaGregorian(date.year - 1);
    // Bright Week: Pascha through Pascha+6.
    if (isOnOrAfter(date, thisYearPascha) && diffDays(thisYearPascha, date) < 7) {
        return {
            tone: null,
            season: 'Bright Week',
            anchorAntipascha: null,
            weeksSinceAntipascha: null,
        };
    }
    // Holy Week: Pascha-7 through Pascha-1.
    const holyWeekStart = addDays(thisYearPascha, -7);
    if (isOnOrAfter(date, holyWeekStart) && isBefore(date, thisYearPascha)) {
        return { tone: null, season: 'Holy Week', anchorAntipascha: null, weeksSinceAntipascha: null };
    }
    // Triodion (pre-Lent + Lent): Pascha-70 through Pascha-8 inclusive.
    const triodionStart = addDays(thisYearPascha, TRIODION_OFFSET_FROM_PASCHA);
    if (isOnOrAfter(date, triodionStart) && isBefore(date, holyWeekStart)) {
        return {
            tone: null,
            season: 'Triodion (pre-Lent and Lent)',
            anchorAntipascha: null,
            weeksSinceAntipascha: null,
        };
    }
    // Otherwise: the standard Octoechos cycle anchored to the most recent
    // Antipascha. For dates earlier in the year, that's last year's Antipascha.
    const thisYearAntipascha = addDays(thisYearPascha, 7);
    const anchor = isOnOrAfter(date, thisYearAntipascha)
        ? thisYearAntipascha
        : addDays(lastYearPascha, 7);
    const daysSince = diffDays(anchor, date);
    const weeksSince = Math.floor(daysSince / 7);
    const tone = (((weeksSince % 8) + 8) % 8) + 1;
    let season = 'Pentecostarion';
    if (weeksSince > 7)
        season = 'Octoechos';
    else if (weeksSince === 0)
        season = 'Antipascha (Tone 1)';
    return {
        tone,
        season,
        anchorAntipascha: anchor,
        weeksSinceAntipascha: weeksSince,
    };
}
//# sourceMappingURL=tones.js.map